/**
 * AI Design Inspiration — shared showcase module.
 *
 * Used on:
 *   - The AI Designer page (a horizontal "rail" rendered above the chat).
 *   - The standalone /pages/inspiration page (full responsive grid).
 *
 * The list is curated by Roland. He flips himself into admin mode by visiting
 * any page with ?admin=YOUR_TOKEN once — the token is stashed in localStorage,
 * the URL is cleaned up, and admin-only buttons (promote / remove) become
 * visible on subsequent visits. Use ?admin=logout to clear the flag.
 *
 * All requests use XHR — Shopify's pixel scripts wrap window.fetch, which
 * we've found breaks cross-origin responses on this storefront.
 */
(function (global) {
  const ADMIN_KEY = "ai_design_admin_token_v1";

  // ============= Admin token =============
  function getAdminToken() {
    try { return localStorage.getItem(ADMIN_KEY) || ""; }
    catch (_) { return ""; }
  }

  function setAdminToken(token) {
    try {
      if (token) localStorage.setItem(ADMIN_KEY, token);
      else localStorage.removeItem(ADMIN_KEY);
    } catch (_) {}
  }

  function isAdmin() { return !!getAdminToken(); }

  /**
   * Reads ?admin=<TOKEN> from the URL once, stashes it, and removes the
   * param so the token doesn't sit in the address bar. Pass ?admin=logout
   * to clear the flag. After processing, render the admin pill so Roland
   * can always see whether he's currently flipped.
   */
  function maybeSetAdminFromQuery() {
    try {
      const url = new URL(global.location.href);
      const adm = url.searchParams.get("admin");
      if (adm !== null) {
        if (adm === "logout" || adm === "") {
          setAdminToken("");
        } else {
          setAdminToken(adm);
        }
        url.searchParams.delete("admin");
        global.history.replaceState({}, "", url.toString());
      }
    } catch (_) {}
    // Always try to render the pill — it's a no-op when not admin.
    renderAdminPill();
  }

  /**
   * Renders a small floating "Admin mode" pill in the bottom-right corner.
   * Visible only when the admin token is set. Click "Sign out" to clear.
   * Renders once per page; safe to call multiple times.
   */
  function renderAdminPill() {
    if (typeof document === "undefined") return;
    function inject() {
      if (document.getElementById("ai-admin-pill")) return;
      if (!isAdmin()) return;
      if (!document.body) {
        // Body not ready yet — defer.
        document.addEventListener("DOMContentLoaded", inject, { once: true });
        return;
      }
      const pill = document.createElement("div");
      pill.id = "ai-admin-pill";
      pill.className = "ai-admin-pill";
      pill.innerHTML =
        '<span class="ai-admin-pill__dot" aria-hidden="true"></span>' +
        '<span class="ai-admin-pill__label">Admin mode</span>' +
        '<button type="button" class="ai-admin-pill__logout">Sign out</button>';
      pill.querySelector(".ai-admin-pill__logout").addEventListener("click", function () {
        if (!global.confirm("Sign out of admin mode? You'll need your token to log back in.")) return;
        setAdminToken("");
        pill.remove();
        global.location.reload();
      });
      document.body.appendChild(pill);
    }
    inject();
  }

  // ============= API calls (XHR) =============
  function xhrJson(method, url, opts) {
    opts = opts || {};
    return new Promise(function (resolve, reject) {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.setRequestHeader("Accept", "application/json");
      if (opts.body) xhr.setRequestHeader("Content-Type", "application/json");
      if (opts.token) xhr.setRequestHeader("Authorization", "Bearer " + opts.token);
      xhr.timeout = opts.timeoutMs || 15000;
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText || "{}")); }
          catch (e) { reject(e); }
        } else {
          let msg = "HTTP " + xhr.status;
          try {
            const err = JSON.parse(xhr.responseText);
            if (err && err.error) msg = err.error;
          } catch (_) {}
          reject(new Error(msg));
        }
      };
      xhr.onerror = function () { reject(new Error("Network error")); };
      xhr.ontimeout = function () { reject(new Error("Timed out")); };
      xhr.send(opts.body || null);
    });
  }

  function fetchList(workerUrl) {
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("GET", base + "/api/showcase").then(function (data) {
      return Array.isArray(data && data.items) ? data.items : [];
    });
  }

  function promote(workerUrl, payload) {
    const token = getAdminToken();
    if (!token) return Promise.reject(new Error("Admin token not set"));
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("POST", base + "/api/showcase/promote", {
      token: token,
      body: JSON.stringify(payload || {}),
    });
  }

  function removeItem(workerUrl, id) {
    const token = getAdminToken();
    if (!token) return Promise.reject(new Error("Admin token not set"));
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("DELETE", base + "/api/showcase/" + encodeURIComponent(id), {
      token: token,
    });
  }

  /** Customer-side: submit a design to the moderation queue. Public, no token. */
  function submit(workerUrl, payload) {
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("POST", base + "/api/showcase/submit", {
      body: JSON.stringify(payload || {}),
    });
  }

  /** Public: get live counts of submissions for the "X remaining" counter. */
  function fetchPromoStats(workerUrl) {
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("GET", base + "/api/showcase/promo-stats");
  }

  /** Admin: list pending submissions awaiting approval. */
  function fetchPending(workerUrl) {
    const token = getAdminToken();
    if (!token) return Promise.reject(new Error("Admin token not set"));
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("GET", base + "/api/showcase/pending", { token: token })
      .then(function (data) {
        return Array.isArray(data && data.items) ? data.items : [];
      });
  }

  /** Admin: approve a pending submission, moving it into the live showcase. */
  function approve(workerUrl, id) {
    const token = getAdminToken();
    if (!token) return Promise.reject(new Error("Admin token not set"));
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("POST", base + "/api/showcase/approve", {
      token: token,
      body: JSON.stringify({ id: id }),
    });
  }

  /** Admin: reject (delete) a pending submission. */
  function rejectPending(workerUrl, id) {
    const token = getAdminToken();
    if (!token) return Promise.reject(new Error("Admin token not set"));
    const base = (workerUrl || "").replace(/\/$/, "");
    return xhrJson("DELETE", base + "/api/showcase/pending/" + encodeURIComponent(id), {
      token: token,
    });
  }

  /** Track which design URLs the customer has already submitted, locally. */
  const SUBMITTED_KEY = "ai_design_submitted_v1";
  function loadSubmittedSet() {
    try {
      const raw = localStorage.getItem(SUBMITTED_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (_) { return []; }
  }
  function markSubmitted(url) {
    try {
      const set = loadSubmittedSet();
      if (set.indexOf(url) === -1) {
        set.push(url);
        localStorage.setItem(SUBMITTED_KEY, JSON.stringify(set.slice(-100)));
      }
    } catch (_) {}
  }
  function hasSubmitted(url) {
    return loadSubmittedSet().indexOf(url) !== -1;
  }

  /**
   * Promo claim lockout. Set when a customer submits AND the promo was live
   * at submit time. Once set, all promo entry points on this browser hide
   * (so they can't try to claim a second free tee). Admins are exempt for
   * testing — this only affects regular customers.
   */
  const PROMO_CLAIMED_KEY = "ai_design_promo_claimed_v1";
  function hasPromoClaimed() {
    try { return localStorage.getItem(PROMO_CLAIMED_KEY) === "1"; }
    catch (_) { return false; }
  }
  function markPromoClaimed() {
    try { localStorage.setItem(PROMO_CLAIMED_KEY, "1"); }
    catch (_) {}
  }
  /** True when the promo flow should be SUPPRESSED for this visitor. */
  function shouldSuppressPromo() {
    return hasPromoClaimed() && !isAdmin();
  }

  // ============= Tile rendering =============
  /** Build the remix URL for a tile — designer page picks up these params. */
  function remixUrl(designerUrl, item) {
    try {
      const u = new URL(designerUrl || "/pages/design-with-ai", global.location.origin);
      u.searchParams.set("seed_url", item.url);
      if (item.prompt) u.searchParams.set("seed_prompt", item.prompt);
      return u.toString();
    } catch (_) {
      return designerUrl || "/pages/design-with-ai";
    }
  }

  /**
   * Render a single tile. Used by both the rail and the grid. Showcase-only:
   * tiles are non-interactive cards (no remix click), just image + caption.
   */
  function renderTile(item, opts) {
    opts = opts || {};
    const tile = document.createElement("li");
    tile.className = "ai-insp-tile";
    tile.dataset.id = item.id;

    const card = document.createElement("div");
    card.className = "ai-insp-tile__card";
    if (item.prompt) card.title = item.prompt;

    const thumbWrap = document.createElement("div");
    thumbWrap.className = "ai-insp-tile__thumb";
    const img = document.createElement("img");
    img.src = item.url;
    img.alt = item.prompt || "AI design";
    img.loading = "lazy";
    img.decoding = "async";
    thumbWrap.appendChild(img);
    card.appendChild(thumbWrap);

    if (item.prompt) {
      const promptEl = document.createElement("p");
      promptEl.className = "ai-insp-tile__prompt";
      promptEl.textContent = item.prompt;
      card.appendChild(promptEl);
    }

    tile.appendChild(card);

    // Admin-only controls — remove + download. Both sit over the top-right
    // of the thumb. Only rendered when isAdmin() is true; customers don't
    // see them, so this isn't a "publicly hidden but technically reachable"
    // download — the buttons aren't in their DOM at all.
    if (isAdmin()) {
      // ── Download (full-resolution image, for marketing / print promos) ──
      const downloadBtn = document.createElement("button");
      downloadBtn.type = "button";
      downloadBtn.className = "ai-insp-tile__download";
      downloadBtn.title = "Download full-res (admin only)";
      downloadBtn.setAttribute("aria-label", "Download design");
      downloadBtn.innerHTML =
        '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
          '<polyline points="7 10 12 15 17 10"/>' +
          '<line x1="12" y1="15" x2="12" y2="3"/>' +
        '</svg>';
      downloadBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        downloadShowcaseImage(item, downloadBtn);
      });
      tile.appendChild(downloadBtn);

      // ── Remove from public showcase ──
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "ai-insp-tile__remove";
      removeBtn.title = "Remove from showcase";
      removeBtn.setAttribute("aria-label", "Remove from showcase");
      removeBtn.textContent = "×";
      removeBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!global.confirm("Remove this from the public showcase?")) return;
        removeBtn.disabled = true;
        removeItem(opts.workerUrl, item.id).then(function () {
          tile.remove();
          if (typeof opts.onRemove === "function") opts.onRemove(item.id);
        }).catch(function (err) {
          global.alert("Couldn't remove: " + (err.message || err));
          removeBtn.disabled = false;
        });
      });
      tile.appendChild(removeBtn);
    }

    return tile;
  }

  /**
   * Download a showcase image as a file (admin only).
   *
   * Routes through the worker's /api/admin/download proxy instead of
   * fetching the R2 URL directly. R2's public bucket doesn't return
   * Access-Control-Allow-Origin headers so direct XHR blobs get blocked
   * by CORS. The worker fetches from R2 server-side and streams back
   * with proper CORS + Content-Disposition headers, so the browser
   * accepts it and triggers a real download.
   */
  function downloadShowcaseImage(item, btn) {
    if (!isAdmin()) return; // double-check
    if (!item || !item.url) return;
    if (btn) {
      btn.disabled = true;
      btn.dataset.originalTitle = btn.title;
      btn.title = "Downloading…";
    }

    // Find the worker URL the same way the page does.
    const workerUrl = (findWorkerUrlOnPage() || "").replace(/\/$/, "");
    if (!workerUrl) {
      global.alert("Worker URL not configured on this page — can't download.");
      if (btn) {
        btn.disabled = false;
        btn.title = btn.dataset.originalTitle || "Download full-res (admin only)";
      }
      return;
    }

    // Extract the R2 object key from the public URL. Tile URLs look like
    // https://pub-xxx.r2.dev/designs/2026-05-09/uuid.png — we want the
    // path after the host.
    let objKey = "";
    try {
      objKey = new URL(item.url).pathname.replace(/^\/+/, "");
    } catch (_) {
      // item.id IS the key in the all-designs view, so fall back to that.
      objKey = item.id || "";
    }
    if (!objKey) {
      global.alert("Couldn't determine the R2 key for this image.");
      if (btn) {
        btn.disabled = false;
        btn.title = btn.dataset.originalTitle || "Download full-res (admin only)";
      }
      return;
    }

    const proxyUrl =
      workerUrl + "/api/admin/download?key=" + encodeURIComponent(objKey);

    // Hit the proxy via XHR with the admin Bearer token. Use blob response
    // type so we get binary bytes back, then trigger a download click.
    const xhr = new XMLHttpRequest();
    xhr.open("GET", proxyUrl, true);
    xhr.responseType = "blob";
    xhr.setRequestHeader("Authorization", "Bearer " + (getAdminToken() || ""));
    xhr.timeout = 90000;
    xhr.onload = function () {
      if (xhr.status < 200 || xhr.status >= 300) {
        let err = "HTTP " + xhr.status;
        try {
          // Worker returns JSON on errors — try to read it.
          const reader = new FileReader();
          reader.onload = function () {
            try {
              const j = JSON.parse(reader.result);
              global.alert("Couldn't download: " + (j.error || err));
            } catch (_) {
              global.alert("Couldn't download: " + err);
            }
            if (btn) {
              btn.disabled = false;
              btn.title = btn.dataset.originalTitle || "Download full-res (admin only)";
            }
          };
          reader.readAsText(xhr.response);
        } catch (_) {
          global.alert("Couldn't download: " + err);
          if (btn) {
            btn.disabled = false;
            btn.title = btn.dataset.originalTitle || "Download full-res (admin only)";
          }
        }
        return;
      }
      const blob = xhr.response;
      const objectUrl = URL.createObjectURL(blob);
      // Filename suggested by Content-Disposition is honoured automatically
      // when we click the anchor, but Safari sometimes ignores it — set a
      // fallback download attribute too.
      const slug = (item.prompt || "showcase")
        .slice(0, 40)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "showcase";
      const fallbackName = "60ftink-" + slug + ".png";
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(objectUrl); }, 30000);
      if (btn) {
        btn.title = "Downloaded ✓";
        setTimeout(function () {
          btn.disabled = false;
          btn.title = btn.dataset.originalTitle || "Download full-res (admin only)";
        }, 2500);
      }
    };
    xhr.onerror = function () {
      global.alert("Couldn't download: network error reaching the worker.");
      if (btn) {
        btn.disabled = false;
        btn.title = btn.dataset.originalTitle || "Download full-res (admin only)";
      }
    };
    xhr.ontimeout = function () {
      global.alert("Download timed out — try again.");
      if (btn) {
        btn.disabled = false;
        btn.title = btn.dataset.originalTitle || "Download full-res (admin only)";
      }
    };
    xhr.send();
  }

  /** Render the horizontal scroll rail (designer page). */
  function renderRail(container, opts) {
    if (!container || !opts || !opts.workerUrl) return Promise.resolve([]);
    return fetchList(opts.workerUrl).then(function (items) {
      if (!items.length) {
        container.style.display = "none";
        return items;
      }
      container.classList.add("ai-insp-rail");
      container.innerHTML = "";
      container.style.display = "";

      const heading = document.createElement("p");
      heading.className = "ai-insp-rail__heading";
      heading.textContent = opts.heading || "Get inspired — remix one of these";
      container.appendChild(heading);

      const list = document.createElement("ul");
      list.className = "ai-insp-rail__list";
      items.forEach(function (item) { list.appendChild(renderTile(item, opts)); });
      container.appendChild(list);
      return items;
    }).catch(function () {
      container.style.display = "none";
      return [];
    });
  }

  /** Render the full grid (inspiration page). */
  function renderGrid(container, opts) {
    if (!container || !opts || !opts.workerUrl) return Promise.resolve([]);
    container.classList.add("ai-insp-grid-wrap");
    container.innerHTML = '<p class="ai-insp-loading">Loading designs…</p>';

    return fetchList(opts.workerUrl).then(function (items) {
      container.innerHTML = "";

      if (!items.length) {
        const empty = document.createElement("div");
        empty.className = "ai-insp-empty";
        const p = document.createElement("p");
        p.textContent = "No designs in the showcase yet — check back soon.";
        empty.appendChild(p);
        if (opts.designerUrl) {
          const cta = document.createElement("a");
          cta.className = "ai-insp-empty__cta";
          cta.href = opts.designerUrl;
          cta.textContent = "Start designing";
          empty.appendChild(cta);
        }
        container.appendChild(empty);
        return items;
      }

      const list = document.createElement("ul");
      list.className = "ai-insp-grid";
      items.forEach(function (item) { list.appendChild(renderTile(item, opts)); });
      container.appendChild(list);
      return items;
    }).catch(function () {
      container.innerHTML = '<p class="ai-insp-error">Couldn\'t load designs. Please refresh.</p>';
      return [];
    });
  }

  // ============= Submit button (customer-side) =============
  /**
   * Attach a "Submit to gallery" button to a chat bubble or gallery tile.
   * Reads promo config from the host element's data attributes (or window
   * defaults). When clicked, opens a modal with email + consent + share
   * checkbox, submits to the worker, and shows the discount code on success.
   */
  function attachSubmitButton(opts) {
    opts = opts || {};
    const cfg = getPromoConfig();
    const promoLive = cfg.enabled || isAdmin();

    // Once a customer has claimed the promo, suppress all promo entry points.
    // Returning null means callers should NOT render this button at all.
    // Admins are exempt so Roland can still test.
    if (promoLive && shouldSuppressPromo()) {
      return null;
    }

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ai-msg__submit-gallery";

    function renderInitial() {
      btn.innerHTML =
        '<span class="ai-msg__submit-icon">★</span>' +
        '<span class="ai-msg__submit-label">' + (promoLive ? "Get my free tee →" : "Submit to gallery") + '</span>';
      btn.title = promoLive
        ? "Submit your design to enter the free-tee promo"
        : "Submit this to our public gallery";
      btn.disabled = false;
    }

    if (hasSubmitted(opts.url)) {
      btn.classList.add("ai-msg__submit-gallery--done");
      btn.innerHTML = '<span>' + (promoLive ? '✓ Code shown above — claim your tee' : '✓ Submitted — thanks!') + '</span>';
      btn.disabled = true;
      return btn;
    }

    renderInitial();

    btn.addEventListener("click", function () {
      if (btn.disabled) return;
      openPromoModal({
        workerUrl: opts.workerUrl,
        designUrl: opts.url,
        prompt: opts.prompt || "",
        onSubmitted: function () {
          markSubmitted(opts.url);
          // If the promo was live at submit time, the customer has now
          // "claimed" their free-tee slot. Lock out further promo entry
          // points on this browser.
          if (promoLive) markPromoClaimed();
          btn.classList.add("ai-msg__submit-gallery--done");
          btn.innerHTML = '<span>' + (promoLive ? '✓ Code shown above — claim your tee' : '✓ Submitted — thanks!') + '</span>';
          btn.disabled = true;
          if (typeof opts.onSubmitted === "function") opts.onSubmitted();
          // Refresh promo banners + counters so they hide too.
          if (typeof updatePromoCounters === "function") updatePromoCounters();
        },
      });
    });

    return btn;
  }

  // ============= Promo config (read from page) =============
  /**
   * Reads promo settings from a hidden `<meta>` tag on the page (rendered by
   * the snippet that injects ai-inspiration.js). Falls back to sensible
   * defaults so things still work if the snippet isn't deployed yet.
   *
   * Expected meta attributes (all optional):
   *   data-promo-enabled       "true" | "false"
   *   data-promo-code          discount code string
   *   data-promo-product-url   /products/promo-tee
   *   data-promo-total-cap     "100"
   *   data-promo-banner-text   "..."
   *   data-promo-credit        "$20"
   *   data-promo-handle        "@60ftink"
   *   data-promo-worker-url    "https://...workers.dev"
   */
  function findWorkerUrlOnPage() {
    // Worker URL lives on whichever AI section is rendering this page —
    // the designer (.ai-chat), my-designs (.my-designs), or the inspiration
    // page (.ai-insp-page). Pick the first one that has it.
    const candidates = [".ai-chat", ".my-designs", ".ai-insp-page"];
    for (let i = 0; i < candidates.length; i++) {
      const el = document.querySelector(candidates[i]);
      if (el && el.dataset.workerUrl) return el.dataset.workerUrl.replace(/\/$/, "");
    }
    return "";
  }

  function getPromoConfig() {
    const meta = document.querySelector("meta[name='ai-promo-config']");
    const workerUrl = findWorkerUrlOnPage();
    if (!meta) {
      return {
        enabled: false,
        code: "",
        productUrl: "",
        totalCap: 100,
        bannerText: "",
        credit: "$20",
        handle: "@60ftink",
        workerUrl: workerUrl,
      };
    }
    return {
      enabled: meta.getAttribute("data-promo-enabled") === "true",
      code: meta.getAttribute("data-promo-code") || "",
      productUrl: meta.getAttribute("data-promo-product-url") || "",
      totalCap: parseInt(meta.getAttribute("data-promo-total-cap") || "100", 10),
      bannerText: meta.getAttribute("data-promo-banner-text") || "",
      credit: meta.getAttribute("data-promo-credit") || "$20",
      handle: meta.getAttribute("data-promo-handle") || "@60ftink",
      normalPrice: meta.getAttribute("data-promo-normal-price") || "$36.63",
      drawEnabled: meta.getAttribute("data-promo-draw-enabled") === "true",
      // Feature flag: hide discount code behind a tee-mockup share?
      // When OFF (default), code shows immediately with a soft share
      // button. When ON, the success modal forces the customer to
      // share before the code reveals. Toggle in Theme settings →
      // AI Design Promo → "Share-to-unlock".
      shareToUnlock: meta.getAttribute("data-promo-share-to-unlock") === "true",
      drawHeadline: meta.getAttribute("data-promo-draw-headline") || "",
      drawDetails: meta.getAttribute("data-promo-draw-details") || "",
      workerUrl: workerUrl,
    };
  }

  // ============= Promo modal (submit flow) =============
  /**
   * Opens the promo submit modal. Captures email, social-share intent, and
   * gallery consent. On successful POST, swaps to the success/code-reveal
   * state showing the discount code and a "Claim your free tee" CTA.
   */
  function openPromoModal(opts) {
    closePromoModal(); // tear down any existing instance

    const cfg = getPromoConfig();
    const promoLive = cfg.enabled || isAdmin();
    const overlay = document.createElement("div");
    overlay.className = "ai-promo-modal";
    overlay.innerHTML = renderPromoModalForm(cfg, promoLive, opts);
    document.body.appendChild(overlay);
    document.body.classList.add("ai-promo-modal-open");

    // Close handlers
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closePromoModal();
    });
    overlay.querySelector("[data-promo-close]").addEventListener("click", closePromoModal);
    document.addEventListener("keydown", escapeHandler);

    // Submit handler
    const form = overlay.querySelector("[data-promo-form]");
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      const emailEl = form.querySelector("[name='email']");
      const consentEl = form.querySelector("[name='consent']");
      const intentEl = form.querySelector("[name='intent']");
      const errEl = overlay.querySelector("[data-promo-error]");
      const submitBtn = overlay.querySelector("[data-promo-submit]");

      errEl.textContent = "";

      const email = (emailEl.value || "").trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errEl.textContent = "Please enter a valid email address.";
        emailEl.focus();
        return;
      }
      if (!consentEl.checked) {
        errEl.textContent = "Please confirm you're happy for your design to appear publicly.";
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = "Submitting…";

      submit(opts.workerUrl, {
        url: opts.designUrl,
        prompt: opts.prompt,
        email: email,
        socialIntent: intentEl.checked,
        galleryConsent: consentEl.checked,
      }).then(function () {
        // Swap modal contents to success/reveal state.
        overlay.innerHTML = renderPromoModalSuccess(cfg, promoLive, opts);
        overlay.querySelector("[data-promo-close]").addEventListener("click", closePromoModal);

        // Branch: which share-flow does the rendered modal need wiring for?
        // The renderer chose between unlock and soft-share based on
        // cfg.shareToUnlock. Wire the corresponding handler — wrapped in
        // try/catch so a failure here can never break the entry-point
        // setup elsewhere on the page.
        try {
          if (promoLive && cfg.shareToUnlock) {
            wirePromoShareUnlock(overlay, cfg, opts, email);
          } else if (promoLive) {
            wirePromoSoftShare(overlay, cfg, opts, email);
          }
        } catch (e) {
          console.error("[ai-promo] share wiring failed (fail-open):", e);
        }

        if (typeof opts.onSubmitted === "function") opts.onSubmitted();
      }).catch(function (err) {
        errEl.textContent = err && err.message ? err.message : "Submit failed — please try again.";
        submitBtn.disabled = false;
        submitBtn.textContent = promoLive ? "Get my tee" : "Submit my design";
      });
    });

    // Focus the email input on open
    setTimeout(function () {
      const emailEl = overlay.querySelector("[name='email']");
      if (emailEl) emailEl.focus();
    }, 50);
  }

  function escapeHandler(e) {
    if (e.key === "Escape") closePromoModal();
  }

  function closePromoModal() {
    const existing = document.querySelector(".ai-promo-modal");
    if (existing) existing.remove();
    document.body.classList.remove("ai-promo-modal-open");
    document.removeEventListener("keydown", escapeHandler);
  }

  function escapeHtml(s) {
    return String(s || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  function renderPromoModalForm(cfg, promoLive, opts) {
    const adminBadge = !cfg.enabled && isAdmin()
      ? '<div class="ai-promo-modal__admin-badge">PROMO PREVIEW · Customers don\'t see this yet</div>'
      : '';
    const normalPrice = escapeHtml(cfg.normalPrice || "$36.63");
    const promoHeader = promoLive
      ? '<h2 class="ai-promo-modal__title">Get your free tee 🎉</h2>' +
        '<p class="ai-promo-modal__subtitle">Normally <s>' + normalPrice + '</s> + shipping. Submit your design and we\'ll send you a code for a <strong>FREE tee</strong> &mdash; just pay postage.</p>'
      : '<h2 class="ai-promo-modal__title">Submit to gallery</h2>' +
        '<p class="ai-promo-modal__subtitle">Add your design to our public Inspiration page.</p>';

    return (
      '<div class="ai-promo-modal__sheet" role="dialog" aria-modal="true" aria-labelledby="ai-promo-title">' +
        adminBadge +
        '<button type="button" class="ai-promo-modal__close" data-promo-close aria-label="Close">×</button>' +
        '<div class="ai-promo-modal__preview">' +
          '<img src="' + escapeHtml(opts.designUrl) + '" alt="Your design" />' +
        '</div>' +
        '<div class="ai-promo-modal__body">' +
          promoHeader +
          '<form data-promo-form class="ai-promo-modal__form" novalidate>' +
            '<label class="ai-promo-modal__field">' +
              '<span class="ai-promo-modal__label">Your email <span class="ai-promo-modal__req">*</span></span>' +
              '<input type="email" name="email" required autocomplete="email" placeholder="you@example.com">' +
              '<span class="ai-promo-modal__hint">We\'ll send your discount code here. You\'ll also be added to our mailing list for design tips and drops.</span>' +
            '</label>' +
            '<label class="ai-promo-modal__check">' +
              '<input type="checkbox" name="consent" required>' +
              '<span>I\'m happy for my design to appear publicly in the 60ft Ink gallery. <span class="ai-promo-modal__req">*</span></span>' +
            '</label>' +
            '<label class="ai-promo-modal__check">' +
              '<input type="checkbox" name="intent" checked>' +
              '<span>I\'ll share my design on Instagram or Facebook and tag <strong>' + escapeHtml(cfg.handle) + '</strong> to help spread the word.</span>' +
            '</label>' +
            '<div class="ai-promo-modal__error" data-promo-error role="alert" aria-live="polite"></div>' +
            '<button type="submit" class="ai-promo-modal__submit" data-promo-submit>' +
              (promoLive ? 'Get my tee' : 'Submit to gallery') +
            '</button>' +
            '<p class="ai-promo-modal__fine-print">' +
              (promoLive
                ? 'Limit one free tee per customer. While stocks last. By submitting, you agree to our terms of use.'
                : 'Your design may be featured in our public gallery. By submitting, you agree to our terms of use.') +
            '</p>' +
          '</form>' +
        '</div>' +
      '</div>'
    );
  }

  function renderPromoModalSuccess(cfg, promoLive, opts) {
    const adminBadge = !cfg.enabled && isAdmin()
      ? '<div class="ai-promo-modal__admin-badge">PROMO PREVIEW · Customers don\'t see this yet</div>'
      : '';

    if (!promoLive) {
      // Plain submit-thanks (no promo running)
      return (
        '<div class="ai-promo-modal__sheet ai-promo-modal__sheet--success" role="dialog">' +
          '<button type="button" class="ai-promo-modal__close" data-promo-close aria-label="Close">×</button>' +
          '<div class="ai-promo-modal__success">' +
            '<div class="ai-promo-modal__success-icon">✓</div>' +
            '<h2 class="ai-promo-modal__title">Submitted!</h2>' +
            '<p class="ai-promo-modal__subtitle">Your design will appear in the gallery once we\'ve reviewed it. Thanks for sharing.</p>' +
            '<button type="button" class="ai-promo-modal__cta" data-promo-close>Close</button>' +
          '</div>' +
        '</div>'
      );
    }

    // Full promo reveal. TWO RENDER PATHS:
    //   1. cfg.shareToUnlock === true  → "share-to-unlock" UI (locked
    //      step 1 → reveal code on share); only flip ON after testing.
    //   2. otherwise → soft-share UI (code shown immediately + share
    //      button below as a gentle nudge). Default + safe path.
    // Done as an upstream branch so the unlock UI is fully isolated and
    // can't accidentally affect the soft-share path.
    const safeProductUrl = escapeHtml(cfg.productUrl || "/collections/all");
    const safeCode = escapeHtml(cfg.code || "AIFREESHIRT");
    const normalPriceSafe = escapeHtml(cfg.normalPrice || "$36.63");
    const handleSafe = escapeHtml(cfg.handle || "@60ftink");

    if (cfg.shareToUnlock) {
      return renderPromoModalSuccessUnlock(cfg, opts, {
        adminBadge: adminBadge,
        safeProductUrl: safeProductUrl,
        safeCode: safeCode,
        normalPriceSafe: normalPriceSafe,
        handleSafe: handleSafe,
      });
    }

    return (
      '<div class="ai-promo-modal__sheet ai-promo-modal__sheet--success" role="dialog">' +
        adminBadge +
        '<button type="button" class="ai-promo-modal__close" data-promo-close aria-label="Close">×</button>' +
        '<div class="ai-promo-modal__success">' +
          '<div class="ai-promo-modal__success-icon">🎉</div>' +
          '<h2 class="ai-promo-modal__title">You\'re in!</h2>' +
          '<p class="ai-promo-modal__subtitle">Your design is in the queue. Use this code at checkout for a <strong>free tee</strong> (normally ' + normalPriceSafe + ') &mdash; just pay postage.</p>' +
          '<div class="ai-promo-modal__code-box">' +
            '<span class="ai-promo-modal__code-label">Your code</span>' +
            '<code class="ai-promo-modal__code">' + safeCode + '</code>' +
          '</div>' +
          '<a class="ai-promo-modal__cta" href="' + safeProductUrl + '">Claim your free tee →</a>' +
          '<div class="ai-promo-modal__share-block">' +
            '<div class="ai-promo-modal__share-header">' +
              '<span class="ai-promo-modal__share-step">Step 2</span>' +
              '<strong>Share your design — it\'s part of the deal</strong>' +
            '</div>' +
            '<p class="ai-promo-modal__share-pitch">' +
              'You got the tee free in exchange for showing it off. Hit share now and tag <strong>' + handleSafe + '</strong> ' +
              '— a 30-second post is what makes this whole thing work.' +
            '</p>' +
            '<button type="button" class="ai-promo-modal__share" data-promo-share>' +
              '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>' +
                '<polyline points="16 6 12 2 8 6"/>' +
                '<line x1="12" y1="2" x2="12" y2="15"/>' +
              '</svg>' +
              '<span>Share my design now</span>' +
            '</button>' +
          '</div>' +
          '<div class="ai-promo-modal__bonus">' +
            '<span class="ai-promo-modal__bonus-icon" aria-hidden="true">⛽</span>' +
            '<div>' +
              '<strong class="ai-promo-modal__bonus-title">Then: wear it &amp; post it for the $200 fuel card draw</strong>' +
              '<p class="ai-promo-modal__bonus-text">When your tee arrives, post a pic of <strong>YOU wearing it</strong> tagged ' + handleSafe + ' on Instagram or Facebook. Every wear-and-post entry goes into our <strong>monthly $200 fuel card draw</strong> — one winner picked at random each month.</p>' +
            '</div>' +
          '</div>' +
          '<p class="ai-promo-modal__fine-print">' +
            'Limit one per customer. While stocks last.' +
          '</p>' +
        '</div>' +
      '</div>'
    );
  }

  // ============= Promo: soft share (default) =============
  /**
   * Wire the soft-share button on the success modal. Code is already
   * shown — this is a gentle nudge to share. Tracks the click for
   * reporting and opens the native share sheet (or AIDesignShare's
   * fallback modal). Defensive — any failure here is swallowed so
   * the modal still works.
   */
  function wirePromoSoftShare(overlay, cfg, opts, email) {
    var shareBtn = overlay.querySelector("[data-promo-share]");
    if (!shareBtn) return;
    if (!global.AIDesignShare || !global.AIDesignShare.share) return;
    shareBtn.addEventListener("click", function () {
      var handle = cfg.handle || "@60ftink";
      shareBtn.disabled = true;
      shareBtn.innerHTML = '<span>Preparing share…</span>';

      if (cfg.workerUrl) {
        try {
          fetch(cfg.workerUrl + '/api/promo/share-click', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email, designUrl: opts.designUrl }),
            keepalive: true,
          }).catch(function () {});
        } catch (_) { /* ignore */ }
      }

      global.AIDesignShare.share({
        url: opts.designUrl,
        prompt: opts.prompt || "",
        text:
          "Made this in a few minutes with " + handle +
          "'s AI designer — and I didn't need to hire a designer 🎨",
      }).finally(function () {
        shareBtn.innerHTML = '<span>✓ Shared — thanks!</span>';
        shareBtn.disabled = true;
      });
    });
  }

  // ============= Promo: share-to-unlock HTML renderer =============
  /**
   * Render the LOCKED success modal — code is hidden behind a tee-mockup
   * share. Used only when cfg.shareToUnlock is true. Returns the same
   * sheet shell + close button as the soft-share variant; differs in
   * what's inside .ai-promo-modal__success.
   *
   * `bits` carries pre-escaped values from the caller so this fn can
   * stay synchronous string concatenation only.
   */
  function renderPromoModalSuccessUnlock(cfg, opts, bits) {
    return (
      '<div class="ai-promo-modal__sheet ai-promo-modal__sheet--success" role="dialog">' +
        bits.adminBadge +
        '<button type="button" class="ai-promo-modal__close" data-promo-close aria-label="Close">×</button>' +
        '<div class="ai-promo-modal__success">' +

          // ── STEP 1: Share-to-unlock (visible) ──────────────────
          '<div class="ai-promo-unlock" data-promo-unlock>' +
            '<div class="ai-promo-unlock__step">Step 1 of 2</div>' +
            '<h2 class="ai-promo-modal__title">Share your tee mockup to unlock the code</h2>' +
            '<p class="ai-promo-modal__subtitle">Your free tee was earned by spreading the word. Hit share, tag <strong>' + bits.handleSafe + '</strong>, and your discount code unlocks instantly.</p>' +
            '<div class="ai-promo-mockup-wrap" data-promo-mockup>' +
              '<div class="ai-promo-mockup__loading">Building your mockup…</div>' +
            '</div>' +
            '<button type="button" class="ai-promo-unlock__share" data-promo-share-unlock disabled>' +
              '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>' +
                '<polyline points="16 6 12 2 8 6"/>' +
                '<line x1="12" y1="2" x2="12" y2="15"/>' +
              '</svg>' +
              '<span data-share-label>Preparing share…</span>' +
            '</button>' +
            '<p class="ai-promo-unlock__hint" data-share-hint>The mockup is rendering — share button activates in a moment.</p>' +
          '</div>' +

          // ── STEP 2: Code reveal (initially hidden) ─────────────
          '<div class="ai-promo-revealed" data-promo-revealed hidden>' +
            '<div class="ai-promo-modal__success-icon">🎉</div>' +
            '<h2 class="ai-promo-modal__title">Thanks for sharing — code unlocked</h2>' +
            '<p class="ai-promo-modal__subtitle">Use this at checkout for a <strong>free tee</strong> (normally ' + bits.normalPriceSafe + ') &mdash; just pay postage.</p>' +
            '<div class="ai-promo-modal__code-box">' +
              '<span class="ai-promo-modal__code-label">Your code</span>' +
              '<code class="ai-promo-modal__code">' + bits.safeCode + '</code>' +
            '</div>' +
            '<a class="ai-promo-modal__cta" href="' + bits.safeProductUrl + '">Claim your free tee →</a>' +
            '<div class="ai-promo-modal__bonus">' +
              '<span class="ai-promo-modal__bonus-icon" aria-hidden="true">⛽</span>' +
              '<div>' +
                '<strong class="ai-promo-modal__bonus-title">Then: wear it &amp; post it for the $200 fuel card draw</strong>' +
                '<p class="ai-promo-modal__bonus-text">When your tee arrives, post a pic of <strong>YOU wearing it</strong> tagged ' + bits.handleSafe + ' on Instagram or Facebook. Every wear-and-post entry goes into our <strong>monthly $200 fuel card draw</strong> — one winner picked at random each month.</p>' +
              '</div>' +
            '</div>' +
            '<p class="ai-promo-modal__fine-print">Limit one per customer. While stocks last.</p>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  // ============= Promo: share-to-unlock wiring =============
  /**
   * Drive the share-to-unlock flow on the success modal:
   *   1. Render the tee mockup canvas inline (using AIDesignMockup
   *      exposed by ai-design-attach.js).
   *   2. Once the mockup is ready, enable the share button and prep
   *      a File from the canvas blob for navigator.share.
   *   3. On click → real Web Share if files supported, otherwise
   *      fallback flow (download mockup + "I posted" confirmation).
   *   4. On confirmed share → reveal the code section.
   *
   * Hard gate: the code stays hidden until step 4. Roland wants the
   * tradeoff — slightly more friction at unlock for dramatically
   * higher real-world share rate.
   */
  function wirePromoShareUnlock(overlay, cfg, opts, email) {
    var mockupWrap = overlay.querySelector("[data-promo-mockup]");
    var shareBtn = overlay.querySelector("[data-promo-share-unlock]");
    var shareLabel = overlay.querySelector("[data-share-label]");
    var shareHint = overlay.querySelector("[data-share-hint]");
    var unlockSection = overlay.querySelector("[data-promo-unlock]");
    var revealedSection = overlay.querySelector("[data-promo-revealed]");
    var handle = cfg.handle || "@60ftink";
    var captionText =
      "Made this in a few minutes with " + handle +
      "'s AI designer — my new tee 🎨";

    if (!mockupWrap || !shareBtn) {
      revealCode();
      return;
    }

    var mockupFile = null;
    var mockupCanvas = null;
    var shareCompleted = false;

    function revealCode() {
      if (unlockSection) unlockSection.hidden = true;
      if (revealedSection) revealedSection.removeAttribute("hidden");
    }

    function setShareLabel(text) {
      if (shareLabel) shareLabel.textContent = text;
    }

    function recordShareClick() {
      if (!cfg.workerUrl) return;
      try {
        fetch(cfg.workerUrl + "/api/promo/share-click", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email, designUrl: opts.designUrl }),
          keepalive: true,
        }).catch(function () {});
      } catch (_) { /* ignore */ }
    }

    // ── Activate the button right away with a "share design now" label.
    //    The mockup compose runs in the background and upgrades the label
    //    + the file-to-share once it's ready. This way the button is NEVER
    //    permanently disabled regardless of what happens in compose.
    shareBtn.disabled = false;
    setShareLabel("Share to unlock my code");
    if (shareHint) shareHint.textContent = "Share with " + handle + " to unlock your discount code.";

    // ── Background: try to build a mockup. Promise chain (no async) for
    //    maximum compatibility with whatever Shopify's minifier does.
    function buildMockup() {
      if (!window.AIDesignMockup || !window.AIDesignMockup.composeFront) {
        return Promise.reject(new Error("Mockup composer not loaded"));
      }
      return window.AIDesignMockup.composeFront(opts.designUrl)
        .then(function (canvas) {
          mockupCanvas = canvas;
          if (mockupWrap) {
            mockupWrap.innerHTML = "";
            canvas.classList.add("ai-promo-mockup__canvas");
            mockupWrap.appendChild(canvas);
          }
          // Try to extract a Blob. The product image is cross-origin
          // (Shopify CDN) and may taint the canvas, in which case toBlob
          // throws SecurityError. Wrap in try/catch + handle the null.
          return new Promise(function (resolve) {
            try {
              canvas.toBlob(function (blob) { resolve(blob); }, "image/png", 0.92);
            } catch (e) {
              console.warn("toBlob threw (canvas tainted?):", e);
              resolve(null);
            }
          });
        })
        .then(function (blob) {
          if (!blob) {
            // Canvas is fine for display but blob extraction failed (likely
            // tainted). Keep the visual mockup, just won't share as a file.
            if (shareHint) {
              shareHint.textContent =
                "Mockup ready — sharing will use your design link. Tag " + handle + " when you post.";
            }
            return;
          }
          var slug = (opts.prompt || "design")
            .slice(0, 32)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "design";
          try {
            mockupFile = new File([blob], "60ftink-mockup-" + slug + ".png", {
              type: "image/png",
            });
            setShareLabel("Share my mockup to unlock");
            if (shareHint) {
              shareHint.textContent = "Mockup ready — share it tagged " + handle + " and your code unlocks.";
            }
          } catch (e) {
            // File constructor unsupported on some old browsers — that's OK,
            // we'll fall through to URL-only share path on click.
            console.warn("File constructor failed:", e);
          }
        });
    }

    buildMockup().catch(function (err) {
      console.warn("Mockup pipeline failed (button still works):", err);
      if (mockupWrap) {
        mockupWrap.innerHTML =
          '<div class="ai-promo-mockup__error">Mockup preview unavailable — share your design link instead.</div>';
      }
    });

    // ── Click handler: explicit share-platform panel. ──
    // Instead of trying to use the Web Share API (silent clipboard fallback
    // on desktop = "Thanks for sharing" without anything actually being
    // shared), we now swap the unlock section to a CLEAR panel with:
    //   • A "Share to Facebook" button that opens the FB sharer in a new tab
    //   • A "Download for Instagram" button that saves the mockup
    //   • A caption with copy-to-clipboard
    //   • A "I posted — unlock my code" confirm button (manual gate)
    //   • A cancel option
    //
    // The customer sees explicit actions and explicitly confirms. No more
    // "what did I just share and where" confusion.
    shareBtn.addEventListener("click", function () {
      if (shareCompleted) return;
      recordShareClick();
      openSharePanel();
    });

    function openSharePanel() {
      var unlockSection = overlay.querySelector("[data-promo-unlock]");
      if (!unlockSection) {
        // No unlock section — fail open
        shareCompleted = true;
        revealCode();
        return;
      }

      // Build the panel HTML. We stash the original unlock content so
      // "Cancel" can restore it (allowing the customer to retry).
      var originalHTML = unlockSection.innerHTML;
      var fbShareUrl = "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(opts.designUrl);
      var fullCaption = captionText + " " + handle + " #60ftink";

      unlockSection.innerHTML =
        '<div class="ai-promo-share-panel">' +
          '<div class="ai-promo-unlock__step">Final step</div>' +
          '<h2 class="ai-promo-modal__title">Share now to unlock</h2>' +
          '<p class="ai-promo-modal__subtitle">Pick where you want to share. Once posted, click the green button at the bottom and your code unlocks.</p>' +

          '<div class="ai-promo-share-panel__caption">' +
            '<label for="ai-promo-caption-text">Caption (edit it however you like):</label>' +
            '<textarea id="ai-promo-caption-text" class="ai-promo-share-panel__caption-text" rows="3" data-promo-caption-text>' +
              escapeHtml(fullCaption) +
            '</textarea>' +
            '<button type="button" class="ai-promo-share-panel__caption-copy" data-promo-copy-caption>' +
              '📋 Copy caption' +
            '</button>' +
            '<span class="ai-promo-share-panel__caption-status" data-promo-caption-status></span>' +
          '</div>' +

          '<div class="ai-promo-share-panel__actions">' +
            '<a class="ai-promo-share-panel__btn ai-promo-share-panel__btn--fb" href="' + escapeHtml(fbShareUrl) + '" target="_blank" rel="noopener" data-promo-fb-share>' +
              '<span class="ai-promo-share-panel__icon">f</span>' +
              '<span>Share to Facebook</span>' +
            '</a>' +
            '<button type="button" class="ai-promo-share-panel__btn ai-promo-share-panel__btn--ig" data-promo-ig-share>' +
              '<span class="ai-promo-share-panel__icon">⬇</span>' +
              '<span>Download mockup for Instagram</span>' +
            '</button>' +
          '</div>' +

          '<p class="ai-promo-share-panel__instagram-hint">Instagram doesn\'t accept web share — download the image, then post it from the Instagram app and tag <strong>' + escapeHtml(handle) + '</strong>.</p>' +

          '<div class="ai-promo-share-panel__deal-note">' +
            '<strong>Sharing is a condition of the deal.</strong> Your free tee is in exchange for posting and tagging us — please post before unlocking your code.' +
          '</div>' +

          '<div class="ai-promo-share-panel__confirm-row">' +
            '<button type="button" class="ai-promo-share-panel__confirm" data-promo-share-confirm>' +
              '✓ I posted — unlock my code' +
            '</button>' +
            '<button type="button" class="ai-promo-share-panel__back" data-promo-share-back>← Back</button>' +
          '</div>' +
        '</div>';

      // Wire caption copy — reads the CURRENT textarea value, so customer
      // edits are copied (not the original suggestion).
      var captionBtn = unlockSection.querySelector("[data-promo-copy-caption]");
      var captionStatus = unlockSection.querySelector("[data-promo-caption-status]");
      var captionTextarea = unlockSection.querySelector("[data-promo-caption-text]");
      if (captionBtn) {
        captionBtn.addEventListener("click", function () {
          var textToCopy = captionTextarea ? captionTextarea.value : fullCaption;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(textToCopy).then(function () {
              if (captionStatus) captionStatus.textContent = "✓ Copied to clipboard";
              setTimeout(function () {
                if (captionStatus) captionStatus.textContent = "";
              }, 2500);
            }, function () {
              if (captionStatus) captionStatus.textContent = "Copy failed — select the text above and Cmd/Ctrl+C.";
            });
          } else {
            // Fallback: select textarea so customer can copy manually
            if (captionTextarea) {
              captionTextarea.focus();
              captionTextarea.select();
            }
            if (captionStatus) captionStatus.textContent = "Press Cmd/Ctrl+C to copy.";
          }
        });
      }

      // Wire Facebook share — just opens the URL (already an <a> so it works
      // natively, but we add a click hook for analytics-style tracking).
      var fbBtn = unlockSection.querySelector("[data-promo-fb-share]");
      if (fbBtn) {
        fbBtn.addEventListener("click", function () {
          // Promise.resolve so it doesn't block the navigation
          Promise.resolve(recordShareClick());
        });
      }

      // Wire Instagram download — uses the mockup canvas if available,
      // otherwise downloads the bare design URL via an anchor.
      var igBtn = unlockSection.querySelector("[data-promo-ig-share]");
      if (igBtn) {
        igBtn.addEventListener("click", function () {
          downloadShareImage();
        });
      }

      // Wire confirm — unlocks the code.
      var confirmBtn = unlockSection.querySelector("[data-promo-share-confirm]");
      if (confirmBtn) {
        confirmBtn.addEventListener("click", function () {
          shareCompleted = true;
          revealCode();
        });
      }

      // Wire back — restores the original unlock content (with mockup
      // + share button), so customer can try again.
      var backBtn = unlockSection.querySelector("[data-promo-share-back]");
      if (backBtn) {
        backBtn.addEventListener("click", function () {
          unlockSection.innerHTML = originalHTML;
          // Re-wire the share button now that the original content is back.
          // Look up the button again from the restored DOM and re-attach.
          var newShareBtn = unlockSection.querySelector("[data-promo-share-unlock]");
          if (newShareBtn) {
            newShareBtn.disabled = false;
            var newLabel = unlockSection.querySelector("[data-share-label]");
            if (newLabel) newLabel.textContent = "Share to unlock my code";
            newShareBtn.addEventListener("click", function () {
              if (shareCompleted) return;
              recordShareClick();
              openSharePanel();
            });
          }
        });
      }
    }

    function downloadShareImage() {
      // Prefer the mockup canvas, else fall back to fetching the design
      // URL via the worker download proxy if available.
      if (mockupCanvas) {
        try {
          mockupCanvas.toBlob(function (blob) {
            if (blob) {
              triggerBlobDownload(blob, "60ftink-mockup.png");
              return;
            }
            // Tainted canvas — fall back to design URL
            triggerUrlDownload(opts.designUrl, "60ftink-design.png");
          }, "image/png", 0.92);
        } catch (e) {
          triggerUrlDownload(opts.designUrl, "60ftink-design.png");
        }
      } else {
        triggerUrlDownload(opts.designUrl, "60ftink-design.png");
      }
    }

    function triggerBlobDownload(blob, filename) {
      var objUrl = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(objUrl); }, 30000);
    }

    function triggerUrlDownload(url, filename) {
      var a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }

  /**
   * Desktop / unsupported-browser share path. Triggers a download of
   * the mockup, shows the caption text to copy, and reveals the code
   * after the customer confirms they posted.
   *
   * Two buttons:
   *   - "I posted, unlock my code" → calls onConfirm()
   *   - "Cancel" → calls onCancel()
   */
  function openShareFallback(overlay, canvas, file, captionText, handle, onConfirm, onCancel) {
    // Trigger download of the mockup so the customer has the image.
    if (canvas) {
      try {
        canvas.toBlob(function (blob) {
          if (!blob) return;
          const objUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objUrl;
          a.download = file ? file.name : "60ftink-mockup.png";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(function () { URL.revokeObjectURL(objUrl); }, 30000);
        }, "image/png", 0.92);
      } catch (_) { /* ignore */ }
    }

    // Show the fallback panel inside the unlock section.
    const unlockSection = overlay.querySelector("[data-promo-unlock]");
    if (!unlockSection) {
      onConfirm();
      return;
    }
    const fallback = document.createElement("div");
    fallback.className = "ai-promo-unlock__fallback";
    fallback.innerHTML =
      '<div class="ai-promo-unlock__fallback-inner">' +
        '<p><strong>Mockup downloaded ⬇</strong> — your browser doesn\'t support direct sharing, so we sent the image to your Downloads folder.</p>' +
        '<p>Now post it to Instagram or Facebook tagged <strong>' + escapeHtml(handle) + '</strong>, then click below to unlock your code.</p>' +
        '<div class="ai-promo-unlock__caption">' +
          '<label>Suggested caption (tap to copy):</label>' +
          '<button type="button" class="ai-promo-unlock__caption-btn" data-promo-copy-caption>' + escapeHtml(captionText) + ' ' + escapeHtml(handle) + '</button>' +
          '<span class="ai-promo-unlock__caption-status" data-promo-caption-status></span>' +
        '</div>' +
        '<div class="ai-promo-unlock__fallback-actions">' +
          '<button type="button" class="ai-promo-unlock__confirm" data-promo-share-confirm>I posted — unlock my code</button>' +
          '<button type="button" class="ai-promo-unlock__cancel" data-promo-share-cancel>Cancel</button>' +
        '</div>' +
      '</div>';
    unlockSection.appendChild(fallback);

    // Wire the caption copy
    const captionBtn = fallback.querySelector("[data-promo-copy-caption]");
    const captionStatus = fallback.querySelector("[data-promo-caption-status]");
    if (captionBtn) {
      captionBtn.addEventListener("click", function () {
        const text = captionText + " " + handle;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(function () {
            if (captionStatus) captionStatus.textContent = "✓ Copied";
          }).catch(function () {
            if (captionStatus) captionStatus.textContent = "Copy failed — long-press to select.";
          });
        } else {
          if (captionStatus) captionStatus.textContent = "Long-press to select.";
        }
      });
    }

    // Wire the confirm + cancel buttons
    const confirmBtn = fallback.querySelector("[data-promo-share-confirm]");
    const cancelBtn = fallback.querySelector("[data-promo-share-cancel]");
    if (confirmBtn) {
      confirmBtn.addEventListener("click", function () {
        fallback.remove();
        onConfirm();
      });
    }
    if (cancelBtn) {
      cancelBtn.addEventListener("click", function () {
        fallback.remove();
        onCancel();
      });
    }
  }

  // ============= Admin pending queue render =============
  /** Render a single pending tile with Approve / Reject buttons. */
  function renderPendingTile(item, opts) {
    const tile = document.createElement("li");
    tile.className = "ai-insp-tile ai-insp-tile--pending";
    tile.dataset.id = item.id;

    const thumbWrap = document.createElement("div");
    thumbWrap.className = "ai-insp-tile__thumb";
    const img = document.createElement("img");
    img.src = item.url;
    img.alt = item.prompt || "Pending design";
    img.loading = "lazy";
    img.decoding = "async";
    thumbWrap.appendChild(img);
    tile.appendChild(thumbWrap);

    if (item.prompt) {
      const promptEl = document.createElement("p");
      promptEl.className = "ai-insp-tile__prompt";
      promptEl.textContent = item.prompt;
      tile.appendChild(promptEl);
    }

    const actions = document.createElement("div");
    actions.className = "ai-insp-pending__actions";

    const approveBtn = document.createElement("button");
    approveBtn.type = "button";
    approveBtn.className = "ai-insp-pending__approve";
    approveBtn.textContent = "✓ Approve";
    approveBtn.addEventListener("click", function () {
      approveBtn.disabled = true;
      rejectBtn.disabled = true;
      approveBtn.textContent = "Approving…";
      approve(opts.workerUrl, item.id).then(function () {
        tile.remove();
        if (typeof opts.onApprove === "function") opts.onApprove(item);
      }).catch(function (err) {
        approveBtn.textContent = "✓ Approve";
        approveBtn.disabled = false;
        rejectBtn.disabled = false;
        global.alert("Couldn't approve: " + (err.message || err));
      });
    });

    const rejectBtn = document.createElement("button");
    rejectBtn.type = "button";
    rejectBtn.className = "ai-insp-pending__reject";
    rejectBtn.textContent = "✕ Reject";
    rejectBtn.addEventListener("click", function () {
      if (!global.confirm("Reject this submission? It'll be deleted from the queue.")) return;
      rejectBtn.disabled = true;
      approveBtn.disabled = true;
      rejectBtn.textContent = "Rejecting…";
      rejectPending(opts.workerUrl, item.id).then(function () {
        tile.remove();
        if (typeof opts.onReject === "function") opts.onReject(item);
      }).catch(function (err) {
        rejectBtn.textContent = "✕ Reject";
        rejectBtn.disabled = false;
        approveBtn.disabled = false;
        global.alert("Couldn't reject: " + (err.message || err));
      });
    });

    actions.appendChild(approveBtn);
    actions.appendChild(rejectBtn);
    tile.appendChild(actions);

    return tile;
  }

  /** Render the admin-only pending review section. No-op for non-admin. */
  function renderPendingQueue(container, opts) {
    if (!container || !opts || !opts.workerUrl) return Promise.resolve([]);
    if (!isAdmin()) {
      container.style.display = "none";
      return Promise.resolve([]);
    }
    container.classList.add("ai-insp-pending");
    container.innerHTML = '<p class="ai-insp-loading">Loading pending submissions…</p>';

    return fetchPending(opts.workerUrl).then(function (items) {
      container.innerHTML = "";
      if (!items.length) {
        container.style.display = "none";
        return items;
      }
      container.style.display = "";

      const heading = document.createElement("h2");
      heading.className = "ai-insp-pending__heading";
      heading.textContent = "Pending review (" + items.length + ")";
      container.appendChild(heading);

      const subhead = document.createElement("p");
      subhead.className = "ai-insp-pending__subhead";
      subhead.textContent =
        "Customer submissions awaiting approval. Approve to move to the public gallery, reject to delete.";
      container.appendChild(subhead);

      const list = document.createElement("ul");
      list.className = "ai-insp-pending__list";
      items.forEach(function (item) {
        list.appendChild(renderPendingTile(item, opts));
      });
      container.appendChild(list);
      return items;
    }).catch(function (err) {
      container.innerHTML =
        '<p class="ai-insp-error">Couldn\'t load pending queue: ' +
        (err && err.message ? err.message : "unknown error") +
        '</p>';
      return [];
    });
  }

  /**
   * Update any "X of Y left" counters on the page from the worker stats.
   * Reveals the admin-only banner if promo is OFF but the visitor is admin.
   */
  function updatePromoCounters() {
    const cfg = getPromoConfig();
    if (!cfg.workerUrl) return;

    // If the customer has already claimed the promo, suppress every promo
    // surface for them. Admin override applies.
    const suppressed = shouldSuppressPromo();

    // Live banner — hide entirely when suppressed.
    const liveBanner = document.querySelector("[data-ai-promo-banner]");
    if (liveBanner) {
      if (suppressed) liveBanner.setAttribute("hidden", "");
    }

    // Reveal admin-only banner when promo is gated but visitor is admin.
    const adminBanner = document.querySelector("[data-ai-promo-banner-adminonly]");
    if (adminBanner) {
      if (suppressed) {
        adminBanner.setAttribute("hidden", "");
      } else if (isAdmin() && !cfg.enabled) {
        adminBanner.removeAttribute("hidden");
      } else {
        adminBanner.setAttribute("hidden", "");
      }
    }

    // Wear-and-post banner — strictly respects promo_enabled.
    // Previously this had an "isAdmin()" override (admin saw the banner
    // regardless), which confused the "I turned the promo off but it's
    // still showing for me" case. Now: when promo_enabled is OFF, the
    // banner is hidden for everyone including admin. To preview before
    // launch, temporarily flip promo_enabled ON.
    const wearpostBanners = document.querySelectorAll("[data-ai-promo-wearpost]");
    wearpostBanners.forEach(function (el) {
      if (suppressed) el.setAttribute("hidden", "");
      else if (cfg.enabled) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    });

    // Update counter text on any visible banner. We compute remaining as
    // (cap - submission count). Negative values clamp to 0.
    const counters = document.querySelectorAll("[data-ai-promo-counter]");
    if (!counters.length) return;
    fetchPromoStats(cfg.workerUrl).then(function (stats) {
      const used = (stats && stats.totalCount) || 0;
      const remaining = Math.max(0, cfg.totalCap - used);
      counters.forEach(function (el) {
        el.textContent = remaining + " of " + cfg.totalCap + " left";
      });
    }).catch(function () {
      // Silently leave the placeholder text if stats fail.
    });
  }

  // ============= Wear-and-post draw entry =============
  /**
   * Wires "I posted — enter me in the draw" buttons rendered by the
   * wear-and-post snippet. Opens a small modal that takes the customer's
   * email + the URL of their post, validates the URL is from a known
   * social platform, and POSTs to /api/promo/draw-entry.
   *
   * Idempotent — safe to call multiple times.
   */
  function attachDrawEntryButtons() {
    const buttons = document.querySelectorAll("[data-promo-draw-entry-open]");
    buttons.forEach(function (btn) {
      if (btn.dataset.wired === "1") return;
      btn.dataset.wired = "1";
      btn.addEventListener("click", openDrawEntryModal);
    });
  }

  function openDrawEntryModal() {
    closeDrawEntryModal();
    const cfg = getPromoConfig();
    const handle = cfg.handle || "@60ftink";
    const overlay = document.createElement("div");
    overlay.className = "ai-promo-modal ai-promo-draw-modal";
    overlay.innerHTML =
      '<div class="ai-promo-modal__sheet" role="dialog" aria-labelledby="ai-promo-draw-title">' +
        '<button type="button" class="ai-promo-modal__close" data-promo-close aria-label="Close">×</button>' +
        '<div class="ai-promo-modal__body">' +
          '<h2 class="ai-promo-modal__title" id="ai-promo-draw-title">Drop your post link</h2>' +
          '<p class="ai-promo-modal__subtitle">Paste the URL of your Instagram or Facebook post tagged ' + escapeHtml(handle) + ' and you\'re in this month\'s $200 fuel card draw.</p>' +
          '<form data-promo-draw-form>' +
            '<label class="ai-promo-modal__label" for="ai-promo-draw-email">Email you used to claim the tee</label>' +
            '<input type="email" id="ai-promo-draw-email" name="email" class="ai-promo-modal__input" required placeholder="you@example.com" autocomplete="email">' +
            '<label class="ai-promo-modal__label" for="ai-promo-draw-url">Link to your Instagram or Facebook post</label>' +
            '<input type="url" id="ai-promo-draw-url" name="postUrl" class="ai-promo-modal__input" required placeholder="https://www.instagram.com/p/...">' +
            '<label class="ai-promo-modal__label" for="ai-promo-draw-note">Anything we should know? <span class="ai-promo-modal__optional">(optional)</span></label>' +
            '<textarea id="ai-promo-draw-note" name="note" class="ai-promo-modal__textarea" rows="2" maxlength="500" placeholder="e.g. ‘posted to my IG story too’"></textarea>' +
            '<p class="ai-promo-modal__error" data-promo-draw-error></p>' +
            '<button type="submit" class="ai-promo-modal__submit" data-promo-draw-submit>Enter me in the draw</button>' +
          '</form>' +
          '<p class="ai-promo-modal__fine-print">One winner each month, picked at random. We\'ll email you if it\'s you.</p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    document.body.classList.add("ai-promo-modal-open");

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeDrawEntryModal();
    });
    overlay.querySelector("[data-promo-close]").addEventListener("click", closeDrawEntryModal);
    document.addEventListener("keydown", drawEntryEscapeHandler);

    const form = overlay.querySelector("[data-promo-draw-form]");
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      const emailEl = form.querySelector("[name='email']");
      const urlEl = form.querySelector("[name='postUrl']");
      const noteEl = form.querySelector("[name='note']");
      const errEl = overlay.querySelector("[data-promo-draw-error]");
      const submitBtn = overlay.querySelector("[data-promo-draw-submit]");
      errEl.textContent = "";
      const email = (emailEl.value || "").trim();
      const postUrl = (urlEl.value || "").trim();
      const note = (noteEl.value || "").trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errEl.textContent = "Please enter the email you used to claim the tee.";
        emailEl.focus();
        return;
      }
      if (!/^https?:\/\//i.test(postUrl)) {
        errEl.textContent = "Please paste the full URL of your post (starting with https://).";
        urlEl.focus();
        return;
      }
      submitBtn.disabled = true;
      submitBtn.textContent = "Submitting…";

      const workerUrl = (cfg.workerUrl || "").replace(/\/$/, "");
      if (!workerUrl) {
        errEl.textContent = "Site mis-configured — please email us your post link instead.";
        submitBtn.disabled = false;
        submitBtn.textContent = "Enter me in the draw";
        return;
      }

      fetch(workerUrl + "/api/promo/draw-entry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email, postUrl: postUrl, note: note }),
      })
        .then(function (resp) { return resp.json().then(function (j) { return { ok: resp.ok, data: j }; }); })
        .then(function (res) {
          if (!res.ok) {
            errEl.textContent = (res.data && res.data.error) || "Couldn't submit your entry — please try again.";
            submitBtn.disabled = false;
            submitBtn.textContent = "Enter me in the draw";
            return;
          }
          // Swap to success state
          overlay.querySelector(".ai-promo-modal__body").innerHTML =
            '<div class="ai-promo-modal__success">' +
              '<div class="ai-promo-modal__success-icon">🎉</div>' +
              '<h2 class="ai-promo-modal__title">You\'re in the draw!</h2>' +
              '<p class="ai-promo-modal__subtitle">' + escapeHtml(res.data && res.data.message ? res.data.message : "Thanks — winner picked at the end of the month.") + '</p>' +
              '<button type="button" class="ai-promo-modal__cta" data-promo-close>Close</button>' +
            '</div>';
          overlay.querySelector("[data-promo-close]").addEventListener("click", closeDrawEntryModal);
        })
        .catch(function (err) {
          errEl.textContent = (err && err.message) || "Couldn't reach the server — try again in a moment.";
          submitBtn.disabled = false;
          submitBtn.textContent = "Enter me in the draw";
        });
    });

    setTimeout(function () {
      const emailEl = overlay.querySelector("[name='email']");
      if (emailEl) emailEl.focus();
    }, 50);
  }

  function closeDrawEntryModal() {
    const existing = document.querySelector(".ai-promo-draw-modal");
    if (existing) existing.remove();
    document.body.classList.remove("ai-promo-modal-open");
    document.removeEventListener("keydown", drawEntryEscapeHandler);
  }

  function drawEntryEscapeHandler(e) {
    if (e.key === "Escape") closeDrawEntryModal();
  }

  global.AIDesignInspiration = {
    isAdmin: isAdmin,
    setAdminToken: setAdminToken,
    getAdminToken: getAdminToken,
    maybeSetAdminFromQuery: maybeSetAdminFromQuery,
    fetchList: fetchList,
    promote: promote,
    remove: removeItem,
    submit: submit,
    fetchPending: fetchPending,
    approve: approve,
    rejectPending: rejectPending,
    hasSubmitted: hasSubmitted,
    markSubmitted: markSubmitted,
    attachSubmitButton: attachSubmitButton,
    renderPendingQueue: renderPendingQueue,
    renderAdminPill: renderAdminPill,
    fetchPromoStats: fetchPromoStats,
    getPromoConfig: getPromoConfig,
    openPromoModal: openPromoModal,
    closePromoModal: closePromoModal,
    updatePromoCounters: updatePromoCounters,
    attachDrawEntryButtons: attachDrawEntryButtons,
    openDrawEntryModal: openDrawEntryModal,
    closeDrawEntryModal: closeDrawEntryModal,
    remixUrl: remixUrl,
    renderTile: renderTile,
    renderRail: renderRail,
    renderGrid: renderGrid,
  };

  // Render the admin pill on every page that loads this script — so Roland
  // can see his admin status anywhere (designer, inspiration, my-designs).
  // No-op when not admin.
  renderAdminPill();

  // Update any "X of Y left" counters and reveal admin-only banners.
  // Defer until DOM is ready so the data attributes are reachable.
  function onReady() {
    updatePromoCounters();
    attachDrawEntryButtons();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }
})(window);
